// @code-guard: skip-file reason="Legacy G2 chart module pending design-token migration and component breakup"
import React, { FC, useContext, useEffect, useMemo } from 'react';
import { Spin } from 'antd';
import { renderToStaticMarkup } from 'react-dom/server';

import { TrendArrowDown } from 'assets/svg/TrendArrowDown';
import TrendArrowUp from 'assets/svg/TrendArrowUp';
import { colorConstants } from 'config/colorConstants';
import { UserContext } from 'contexts/ConfigContext';
import useTrackTabChange from 'hooks/general/useTrackTabChange';
import { formatNumWithKMBAlt, getConfig, getMaxBound, getMinBound, scaleMaxValue, scaleMinValue } from 'utils/helperMethods';

if (typeof window !== 'undefined') {
    var G2 = require('@antv/g2');
    var { Chart } = G2;
}

interface Props {
    dataset: Array<any>;
    chartContainer: string;
    height: number;
    yAxisTitle?: string;
    xAxisTitle?: string;
    stroke?: string;
    loading?: boolean;
    xRange?: { xMin?: number; xMax?: number };
    yRange?: { xMin?: number; xMax?: number };
    xTickLabels?: string[];
    activeSegmentId?: string | null;
    segmentHighlightConfig?: {
        mode: 'heatMap' | 'segment';
        fillColor: string;
        fillOpacity: number;
        lineColor: string;
        useSegmentAvgLabel?: boolean;
        avgGrowthText?: string;
    };
    onBubbleClick?: (datum: Record<string, unknown>) => void;
    onChartBackgroundClick?: () => void;
}

type BubbleHoverTooltipProps = {
    vendor?: string;
    segment?: string;
    kpis?: Array<{
        id?: string;
        label?: string;
        value?: string;
        growthText?: string;
        trendDirection?: string;
    }>;
};

type GrowthMeta = {
    value: string;
    direction: 'up' | 'down' | 'neutral';
};

type HeatMapValueAverageLabelProps = {
    value?: string;
    metricLabel?: string;
    metricValue?: string;
    metricDirection?: 'up' | 'down' | 'neutral';
};

const X_AXIS_LABEL_CHAR_LIMIT = 14;
const CHART_DECIMAL_PRECISION = 1;

const truncateAxisLabel = (label?: string) => {
    if (!label) return '';
    return label.length > X_AXIS_LABEL_CHAR_LIMIT
        ? `${label.slice(0, X_AXIS_LABEL_CHAR_LIMIT)}...`
        : label;
};

const parseGrowthMeta = (growth?: string, trendDirection?: string): GrowthMeta | null => {
    const value = growth?.trim();
    if (!value || value === '-') return null;
    if (trendDirection === 'down') return { value: value.replace('-', '').replace('+', ''), direction: 'down' };
    if (trendDirection === 'up') return { value: value.replace('-', '').replace('+', ''), direction: 'up' };
    if (value.startsWith('-')) return { value: value.replace('-', ''), direction: 'down' };
    if (value.startsWith('+')) return { value: value.replace('+', ''), direction: 'up' };
    return { value, direction: 'neutral' };
};

const formatYAxisTickLabel = (value: number, decimals: number) => {
    if (!Number.isFinite(value)) return '-';
    if (Math.abs(value) >= 1) {
        return formatNumWithKMBAlt(value, decimals, decimals);
    }
    return value.toFixed(decimals);
};

const toRgba = (color: string, alpha: number) => {
    if (!color) return `rgba(122,63,242,${alpha})`;
    if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        const normalizedHex = hex.length === 3 ? hex.split('').map((char) => `${char}${char}`).join('') : hex;
        if (normalizedHex.length === 6) {
            const r = parseInt(normalizedHex.slice(0, 2), 16);
            const g = parseInt(normalizedHex.slice(2, 4), 16);
            const b = parseInt(normalizedHex.slice(4, 6), 16);
            if (![r, g, b].some((value) => Number.isNaN(value))) return `rgba(${r},${g},${b},${alpha})`;
        }
    }
    return color;
};

const HEAT_MAP_LABEL_TEXT_STYLE: React.CSSProperties = {
    fontSize: '10px',
    lineHeight: '18px',
    fontStyle: 'normal',
    whiteSpace: 'nowrap',
};

const PackArchitectureHeatMapValueAverageLabel: FC<HeatMapValueAverageLabelProps> = ({
    value = '-',
    metricLabel = 'Avg.',
    metricValue = '-',
    metricDirection = 'neutral',
}) => {
    const growthColor = metricDirection === 'down' ? colorConstants.growthDown : metricDirection === 'up' ? colorConstants.growthUp : '#52525c';
    return (
        <div
            style={{
                display: 'inline-flex',
                alignItems: 'flex-start',
                gap: '4px',
                pointerEvents: 'none',
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ ...HEAT_MAP_LABEL_TEXT_STYLE, color: '#71717b', fontWeight: 400 }}>Value</span>
                <span style={{ ...HEAT_MAP_LABEL_TEXT_STYLE, color: '#52525c', fontWeight: 500 }}>{value}</span>
            </div>
            <div
                style={{
                    alignSelf: 'stretch',
                    width: '1px',
                    minHeight: '20px',
                    backgroundColor: '#d9d9d9',
                }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ ...HEAT_MAP_LABEL_TEXT_STYLE, color: '#71717b', fontWeight: 400 }}>{metricLabel}</span>
                <span
                    style={{
                        ...HEAT_MAP_LABEL_TEXT_STYLE,
                        color: metricLabel === 'Growth' ? growthColor : '#52525c',
                        fontWeight: 500,
                    }}
                >
                    {metricValue}
                </span>
            </div>
        </div>
    );
};

// @code-guard: skip-next-function reason="Legacy tooltip UI uses fixed visual palette; tracked for design-token migration"
const PackArchitectureBubbleHoverTooltip: FC<BubbleHoverTooltipProps> = ({
    vendor,
    segment,
    kpis = [],
}) => {
    const visibleKpis = kpis.filter((kpi) => Boolean(kpi.label) || Boolean(kpi.value));
    return (
        <div className="w-[220px] rounded-[12px] bg-white">
            <div className="flex items-center gap-1 pb-1">
                <span className="max-w-[110px] truncate text-[12px] font-semibold text-blackTitle" title={vendor}>
                    {vendor}
                </span>
                <span className="text-[8px] text-blackTitle mx-1">x</span>
                <span className="max-w-[110px] truncate text-[12px] font-semibold text-blackTitle" title={segment}>
                    {segment}
                </span>
            </div>
            <div className="mb-3 mt-2 border-t border-[#E4E4E7]" />

            <div className="flex flex-col gap-2">
                {visibleKpis.map((kpi) => {
                    const growthMeta = parseGrowthMeta(kpi.growthText, kpi.trendDirection);
                    return (
                        <div key={kpi.id || kpi.label} className="flex items-center justify-between">
                            <span className="text-[12px] font-normal text-[#71717b]">{kpi.label || '-'}</span>
                            <div className="flex items-center gap-1">
                                <span className="text-[12px] font-normal text-[#18181b]">{kpi.value || '-'}</span>
                                {growthMeta ? (
                                    <span
                                        className="inline-flex items-center gap-0.5 text-[12px]"
                                        style={{
                                            color:
                                                growthMeta.direction === 'down'
                                                    ? colorConstants.growthDown
                                                    : colorConstants.growthUp,
                                        }}
                                    >
                                        {growthMeta.direction === 'down' ? (
                                            <TrendArrowDown className="shrink-0" color={colorConstants.growthDown} />
                                        ) : (
                                            <TrendArrowUp className="shrink-0" color={colorConstants.growthUp} />
                                        )}
                                        <span>{growthMeta.value}</span>
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
                {visibleKpis.length === 0 ? (
                    <div className="flex items-center justify-between">
                        <span className="text-[12px] font-normal text-[#71717b]">No KPI data</span>
                        <span className="text-[12px] font-normal text-[#18181b]">-</span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const isActiveSegmentRow = (row: Record<string, unknown>, activeSegmentId: string) => {
    const segmentKey = row.segmentKey;
    if (typeof segmentKey === 'string' && segmentKey && segmentKey === activeSegmentId) return true;
    // The "Other Vendor" legend selects an entire column, keyed by columnKey.
    const columnKey = row.columnKey;
    if (typeof columnKey === 'string' && columnKey && columnKey === activeSegmentId) return true;
    return row.segment === activeSegmentId;
};

// @code-guard: skip-next-function reason="Large legacy G2 chart setup pending split into hooks and tokenized constants"
const BubbleScatterChart: FC<Props> = ({
    dataset,
    chartContainer,
    height,
    xAxisTitle,
    yAxisTitle,
    stroke,
    loading,
    xRange = {},
    yRange = {},
    xTickLabels,
    activeSegmentId = null,
    segmentHighlightConfig,
    onBubbleClick,
    onChartBackgroundClick,
}) => {
    const X_FIELD = 'xFieldValue';
    const Y_FIELD = 'yFieldValue';
    const COLOR_FIELD = 'bubbleColor';
    const SIZE_FIELD = 'bubbleSizeValue';

    const isWindowTabActive = useTrackTabChange();
    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const { graphFontSize, graphLabelColor, graphFontFamily } = commonSetting;
    const chartData = useMemo(() => {
        if (!activeSegmentId) return dataset;
        return [...dataset].sort((left, right) => {
            const leftActive = isActiveSegmentRow(left, activeSegmentId) ? 1 : 0;
            const rightActive = isActiveSegmentRow(right, activeSegmentId) ? 1 : 0;
            return leftActive - rightActive;
        });
    }, [activeSegmentId, dataset]);
    const activeSegmentBreakpoints = useMemo(() => {
        if (!activeSegmentId) return null;
        const activeRows = chartData.filter((row) => isActiveSegmentRow(row, activeSegmentId));
        if (activeRows.length === 0) return null;
        const minY = Math.min(...activeRows.map((row) => Number(row[Y_FIELD])));
        const maxY = Math.max(...activeRows.map((row) => Number(row[Y_FIELD])));
        const avgY = activeRows.reduce((sum, row) => sum + Number(row[Y_FIELD]), 0) / activeRows.length;

        if (segmentHighlightConfig?.mode === 'heatMap') {
            return {
                minY,
                maxY,
                avgY,
                segmentColor: segmentHighlightConfig.lineColor,
                regionFillColor: segmentHighlightConfig.fillColor,
                regionFillOpacity: segmentHighlightConfig.fillOpacity,
                useSegmentAvgLabel: Boolean(segmentHighlightConfig.useSegmentAvgLabel),
                avgGrowthText: segmentHighlightConfig.avgGrowthText,
            };
        }

        const segmentBaseColor = String(
            activeRows[0]?.segmentBaseColor ?? activeRows[0]?.[COLOR_FIELD] ?? '#7A3FF2',
        );
        return {
            minY,
            maxY,
            avgY,
            segmentColor: segmentBaseColor,
            regionFillColor: segmentBaseColor,
            regionFillOpacity: 0.13,
            useSegmentAvgLabel: false,
            avgGrowthText: undefined,
        };
    }, [activeSegmentId, chartData, segmentHighlightConfig]);
    const shouldRenderGrowthPctLabels = useMemo(
        () => chartData.some((row) => Boolean(row?.showGrowthPctLabel)),
        [chartData],
    );
    const shouldRenderInfoLabels = useMemo(
        () => chartData.some((row) => Boolean(row?.showBubbleInfoLabel)),
        [chartData],
    );

    useEffect(() => {
        if (!window) return;

        const defaultTooltipConfig = {
            showMarkers: false,
            shared: true,
            showCrosshairs: true,
            crosshairs: {
                type: 'x',
                line: {
                    style: {
                        stroke: '#9CA3AF',
                        lineWidth: 1,
                        lineDash: [0, 0],
                    },
                },
            },
            follow: true,
        };

        const chart = new Chart({
            container: chartContainer,
            autoFit: true,
            height,
            appendPadding: [25, 10, 0, 0],
        });

        chart.data(chartData);

        const minX: any = getMinBound(chartData, [X_FIELD]);
        const minY: any = getMinBound(chartData, [Y_FIELD]);
        const maxX: any = getMaxBound(chartData, [X_FIELD]);
        const maxY: any = getMaxBound(chartData, [Y_FIELD]);
        const xTickCount = Math.max(1, xTickLabels?.length ?? 0);
        // Keep ~half-category breathing room on small datasets, but avoid huge
        // left gap as columns increase after pagination.
        const xScaleRangeStart = Math.min(0.05, 0.5 / xTickCount);
        const xScaleRangeEnd = 1 - xScaleRangeStart;

        chart.scale({
            [X_FIELD]: {
                nice: !xTickLabels || xTickLabels.length === 0,
                min: typeof xRange?.xMin === 'number' ? xRange.xMin : chartData?.length === 1 ? scaleMinValue(10, minX) : null,
                max: typeof xRange?.xMax === 'number' ? xRange.xMax : chartData?.length === 1 ? scaleMaxValue(10, maxX) : null,
                ...(xTickLabels && xTickLabels.length > 0
                    ? {
                          tickInterval: 1,
                      }
                    : {}),
                range: [xScaleRangeStart, xScaleRangeEnd],
            },
            [Y_FIELD]: {
                nice: true,
                min: typeof yRange?.xMin === 'number' ? yRange.xMin : chartData?.length === 1 ? scaleMinValue(10, minY) : null,
                max: typeof yRange?.xMax === 'number' ? yRange.xMax : chartData?.length === 1 ? scaleMaxValue(10, maxY) : null,
                range: [0.05, 0.95],
            },
        });

        chart.tooltip({
            ...defaultTooltipConfig,
            shared: false,
            containerTpl:
                '<div class="g2-tooltip" style="min-width:200px; border-radius:12px; overflow:hidden; background:#ffffff;">' +
                '<div class="g2-tooltip-title" style="margin:10px 0;"></div>' +
                '<ul class="g2-tooltip-list"></ul></div>',
            domStyles: {
                'g2-tooltip': {
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: '#ffffff',
                },
                'g2-tooltip-list': {
                    margin: '0',
                    padding: '0',
                },
            },
            customContent: (_title: string, items: any[] = []) => {
                const datum = items?.[0]?.data || {};
                return renderToStaticMarkup(
                    <PackArchitectureBubbleHoverTooltip
                        vendor={datum.vendor}
                        segment={datum.segment}
                        kpis={datum.tooltipKpis}
                    />,
                );
            },
        });

        chart.axis(X_FIELD, {
            title: {
                text: xAxisTitle,
                style: {
                    fontSize: graphFontSize,
                    fontFamily: commonSetting.graphFontFamily,
                    fill: graphLabelColor,
                    fontWeight: '500',
                },
            },
            label: {
                formatter: (val: any) => {
                    if (xTickLabels && xTickLabels.length > 0) {
                        const roundedIndex = Math.round(Number(val));
                        if (!Number.isNaN(roundedIndex) && roundedIndex >= 1 && roundedIndex <= xTickLabels.length) {
                            return truncateAxisLabel(xTickLabels[roundedIndex - 1]);
                        }
                    }
                    return formatNumWithKMBAlt(val, CHART_DECIMAL_PRECISION, CHART_DECIMAL_PRECISION);
                },
                autoRotate: false,
                autoHide: false,
                style: {
                    fontSize: graphFontSize,
                    fontFamily: commonSetting.graphFontFamily,
                    fill: graphLabelColor,
                },
            },
            grid: {
                line: {
                    style: {
                        stroke: stroke,
                        lineWidth: 0,
                        lineDash: [0, 2],
                    },
                },
            },
        });

        chart.axis(Y_FIELD, {
            title: {
                text: yAxisTitle,
                style: {
                    fontSize: graphFontSize,
                    fontFamily: commonSetting.graphFontFamily,
                    fill: graphLabelColor,
                    fontWeight: '500',
                },
            },
            label: {
                formatter: (val: any) => {
                    return formatYAxisTickLabel(Number(val), CHART_DECIMAL_PRECISION);
                },
                style: {
                    fontSize: graphFontSize,
                    fontFamily: commonSetting.graphFontFamily,
                    fill: graphLabelColor,
                },
            },
            line: {
                style: {
                    stroke: '#D9D9D9',
                    lineWidth: 1,
                },
            },
            grid: {
                line: {
                    style: {
                        stroke: stroke,
                        lineWidth: 1,
                        lineDash: [1, 2],
                    },
                },
            },
        });

        const pointGeometry = chart
            .point()
            .position(`${X_FIELD}*${Y_FIELD}`)
            .size(SIZE_FIELD, [10, 40])
            .color(COLOR_FIELD, (colorValue: string) => colorValue);

        pointGeometry
            .shape('circle')
            .style({
                fields: ['highlightLabel', 'isPinpointSelected'],
                callback: (highlightLabel: string, isPinpointSelected: boolean) => ({
                    lineWidth: isPinpointSelected ? 3 : 1,
                    stroke: '#FFFFFF',
                    strokeOpacity: activeSegmentId ? (highlightLabel ? 1 : 0.45) : 1,
                    fillOpacity: activeSegmentId ? (highlightLabel ? 0.9 : 0.18) : 0.85,
                    cursor: 'pointer',
                }),
            })
            .state({
                active: {
                    style: {
                        lineWidth: 3,
                        stroke: '#FFFFFF',
                        strokeOpacity: 1,
                        fillOpacity: 0.95,
                        cursor: 'pointer',
                    },
                },
            });

        if (shouldRenderGrowthPctLabels) {
            // Canvas-rendered labels (cheap) for the heat-map "growth % per bubble"
            // mode. This can apply to every bubble across all loaded pages, so we
            // must avoid per-bubble HTML annotations here to prevent UI hangs.
            pointGeometry.label('growthText*infoLabelMetricDirection*showGrowthPctLabel', (growthText: string, direction: string, showLabel: boolean) => {
                if (!showLabel || !growthText || growthText === '-') return null;
                const fill =
                    direction === 'down'
                        ? colorConstants.growthDown
                        : direction === 'up'
                          ? colorConstants.growthUp
                          : colorConstants.greyAlt15;
                return {
                    offsetX: 12,
                    offsetY: 0,
                    content: growthText,
                    style: {
                        fontSize: 10,
                        lineHeight: 18,
                        fontWeight: 500,
                        fill,
                        fontFamily: graphFontFamily,
                        textAlign: 'left',
                    },
                };
            });
        } else if (shouldRenderInfoLabels) {
            // HTML annotations only for the active segment's bubbles (bounded count).
            chartData.forEach((datum: any) => {
                if (!datum.showBubbleInfoLabel) return;
                chart.annotation().html({
                    position: [datum[X_FIELD], datum[Y_FIELD]],
                    html: renderToStaticMarkup(
                        <PackArchitectureHeatMapValueAverageLabel
                            value={datum.infoLabelValueText ?? datum.valueText}
                            metricLabel={datum.infoLabelMetric}
                            metricValue={datum.infoLabelMetricValue}
                            metricDirection={datum.infoLabelMetricDirection}
                        />,
                    ),
                    offsetX: 22,
                    offsetY: -8,
                });
            });
        } else {
            pointGeometry.label('bubbleLabel', (label: string) => {
                if (!label) return null;
                const isNegative = String(label).trim().startsWith('-');
                return {
                    offsetX: 10,
                    offsetY: 0,
                    content: label,
                    style: {
                        fontSize: 12,
                        lineHeight: 14,
                        fontWeight: 600,
                        fill: isNegative ? '#ec343e' : '#159957',
                        fontFamily: graphFontFamily,
                        textAlign: 'left',
                    },
                };
            });
        }

        pointGeometry.tooltip('vendor*segment*tooltipKpis', () => ({}));

        chart.interaction('element-active');

        if (activeSegmentBreakpoints) {
            // Extend annotation coordinates to the plot bounds while preserving
            // bubble and X-axis placements (which rely on X scale range insets).
            const xDomainMin = typeof xRange?.xMin === 'number' ? xRange.xMin : minX;
            const xDomainMax = typeof xRange?.xMax === 'number' ? xRange.xMax : maxX;
            const xDomainSpan = Math.max(1, xDomainMax - xDomainMin);
            const xRangeSpan = Math.max(0.01, xScaleRangeEnd - xScaleRangeStart);
            const leftInsetRatio = xScaleRangeStart / xRangeSpan;
            const rightInsetRatio = (1 - xScaleRangeEnd) / xRangeSpan;
            const xStart = xDomainMin - xDomainSpan * leftInsetRatio;
            const xEnd = xDomainMax + xDomainSpan * rightInsetRatio;
            chart.annotation().region({
                start: [xStart, activeSegmentBreakpoints.maxY],
                end: [xEnd, activeSegmentBreakpoints.minY],
                style: {
                    fill: activeSegmentBreakpoints.regionFillColor,
                    fillOpacity: activeSegmentBreakpoints.regionFillOpacity,
                },
                top: false,
            });
            const labelStyle = {
                fill: activeSegmentBreakpoints.segmentColor,
                fontSize: 11,
                fontWeight: 600,
                fontFamily: graphFontFamily,
                textAlign: 'left' as const,
            };
            const addBreakpointGuide = (yValue: number, label?: string) => {
                chart.annotation().line({
                    start: [xStart, yValue],
                    end: [xEnd, yValue],
                    style: {
                        stroke: activeSegmentBreakpoints.segmentColor,
                        lineDash: [4, 4],
                        lineWidth: 1,
                        opacity: 0.95,
                    },
                });
                chart.annotation().text({
                    position: [xStart, yValue],
                    content: label ?? formatNumWithKMBAlt(yValue, CHART_DECIMAL_PRECISION, CHART_DECIMAL_PRECISION),
                    offsetX: -30,
                    offsetY: label ? -2 : 0,
                    style: labelStyle,
                });
            };

            const renderedBreakpointValues: number[] = [];
            const addBreakpointGuideIfUnique = (yValue: number, label?: string) => {
                const hasMatchingValue = renderedBreakpointValues.some((renderedValue) => Math.abs(renderedValue - yValue) < 1e-6);
                if (hasMatchingValue) return;
                renderedBreakpointValues.push(yValue);
                addBreakpointGuide(yValue, label);
            };

            addBreakpointGuideIfUnique(activeSegmentBreakpoints.maxY);
            const avgFormatted = formatNumWithKMBAlt(
                activeSegmentBreakpoints.avgY,
                CHART_DECIMAL_PRECISION,
                CHART_DECIMAL_PRECISION,
            );
            addBreakpointGuideIfUnique(
                activeSegmentBreakpoints.avgY,
                activeSegmentBreakpoints.useSegmentAvgLabel ? `Segment Avg. ${avgFormatted}` : avgFormatted,
            );
            addBreakpointGuideIfUnique(activeSegmentBreakpoints.minY);
        }

        chart.legend(false);
        chart.axis(COLOR_FIELD, false);
        chart.axis(SIZE_FIELD, false);

        // Shared flag so plot:click can tell whether an element was just clicked,
        // preventing it from calling onChartBackgroundClick for bubble clicks.
        let elementClickOccurred = false;

        if (onBubbleClick) {
            chart.on('element:click', (event: { data?: { data?: Record<string, unknown> } }) => {
                elementClickOccurred = true;
                queueMicrotask(() => {
                    elementClickOccurred = false;
                });
                const datum = event?.data?.data;
                if (datum) onBubbleClick(datum);
            });
        }

        if (onChartBackgroundClick) {
            chart.on('plot:click', () => {
                if (elementClickOccurred) return;
                onChartBackgroundClick();
            });
        }

        try {
            chart.render();
        } catch (e) {
            console.error('Bubble chart render error', e);
        }

        return () => {
            chart.destroy();
        };
    }, [
        chartData,
        isWindowTabActive,
        xTickLabels,
        xAxisTitle,
        yAxisTitle,
        xRange?.xMin,
        xRange?.xMax,
        yRange?.xMin,
        yRange?.xMax,
        height,
        stroke,
        activeSegmentBreakpoints,
        activeSegmentId,
        segmentHighlightConfig,
        onBubbleClick,
        onChartBackgroundClick,
        shouldRenderGrowthPctLabels,
        shouldRenderInfoLabels,
    ]);

    return (
        <div>
            {loading ? (
                <div className="flex h-96 w-full items-center justify-center">
                    <Spin />
                </div>
            ) : (
                <div id={chartContainer} />
            )}
        </div>
    );
};
export default BubbleScatterChart;
